import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { MaritimeOrderInfo, OrderInfoType } from 'adoms-common-lib';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomerURLInformationUtil } from '../../common/customerURLInformationUtil';
import { OrderBackNextButtons } from '../../components/atoms/OrderBackNextButtons';
import ApplicationTooBar from '../../components/molecule/ApplicationToolBar';
import OrderStepBar, { Process } from '../../components/molecule/OrderStepBar';
import { FieldKey, TouchedKey, UnitContactFields, UnitKey } from '../../components/organisms/UnitContactFields';
import { OrderInfoNotFound } from '../../components/organisms/OrderInfoNotFound';
import { SectionGrid } from '../../components/organisms/SectionGrid';

export default function MaritimeOrderContactInput() {
    const navigate = useNavigate();
    const location = useLocation();

    const customerURLInformation = CustomerURLInformationUtil.instance.getCustomerURLInformation();
    const order = location.state?.order as MaritimeOrderInfo;
    const orderForEdit = location.state?.orderForEdit as MaritimeOrderInfo;
    const resolvedOrder = (orderForEdit ?? order) as MaritimeOrderInfo | undefined;
    const REQUIRED_MESSAGE = '必須項目です';

    const unitKeys: UnitKey[] = ['hq', 'src', 'dst'];
    const fieldKeys: FieldKey[] = ['Unit', 'LastName', 'FirstName', 'Tel', 'Mail'];

    const createTouchedState = (value: boolean): Record<TouchedKey, boolean> => {
        return unitKeys.reduce((acc, unit) => {
            fieldKeys.forEach((field) => {
                acc[`${unit}${field}` as TouchedKey] = value;
            });
            return acc;
        }, {} as Record<TouchedKey, boolean>);
    };

    const [touched, setTouched] = React.useState<Record<TouchedKey, boolean>>(() => createTouchedState(false));

    const markTouched = (key: TouchedKey) => {
        setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    };

    const markAllTouched = () => {
        setTouched(createTouchedState(true));
    };

    const [supplyHeadquartersUnitName, setSupplyHeadquartersUnitName] = React.useState(
        resolvedOrder?.requester?.supplyHeadquartersInfo?.unitName ?? ''
    );
    const [supplyHeadquartersLastName, setSupplyHeadquartersLastName] = React.useState(
        resolvedOrder?.requester?.supplyHeadquartersInfo?.lastname ?? ''
    );
    const [supplyHeadquartersFirstName, setSupplyHeadquartersFirstName] = React.useState(
        resolvedOrder?.requester?.supplyHeadquartersInfo?.firstname ?? ''
    );
    const [supplyHeadquartersTel, setSupplyHeadquartersTel] = React.useState(
        resolvedOrder?.requester?.supplyHeadquartersInfo?.tel ?? ''
    );
    const [supplyHeadquartersMail, setSupplyHeadquartersMail] = React.useState(
        resolvedOrder?.requester?.supplyHeadquartersInfo?.mailaddress ?? ''
    );

    const [supplySourceUnitName, setSupplySourceUnitName] = React.useState(
        resolvedOrder?.requester?.supplySourceUnitInfo?.unitName ?? ''
    );
    const [supplySourceLastName, setSupplySourceLastName] = React.useState(
        resolvedOrder?.requester?.supplySourceUnitInfo?.lastname ?? ''
    );
    const [supplySourceFirstName, setSupplySourceFirstName] = React.useState(
        resolvedOrder?.requester?.supplySourceUnitInfo?.firstname ?? ''
    );
    const [supplySourceTel, setSupplySourceTel] = React.useState(
        resolvedOrder?.requester?.supplySourceUnitInfo?.tel ?? ''
    );
    const [supplySourceMail, setSupplySourceMail] = React.useState(
        resolvedOrder?.requester?.supplySourceUnitInfo?.mailaddress ?? ''
    );

    const [transportRequestUnitName, setTransportRequestUnitName] = React.useState(
        resolvedOrder?.destination?.transportRequestUnit?.unitName ?? ''
    );
    const [transportRequestLastName, setTransportRequestLastName] = React.useState(
        resolvedOrder?.destination?.transportRequestUnit?.lastname ?? ''
    );
    const [transportRequestFirstName, setTransportRequestFirstName] = React.useState(
        resolvedOrder?.destination?.transportRequestUnit?.firstname ?? ''
    );
    const [transportRequestTel, setTransportRequestTel] = React.useState(
        resolvedOrder?.destination?.transportRequestUnit?.tel ?? ''
    );
    const [transportRequestMail, setTransportRequestMail] = React.useState(
        resolvedOrder?.destination?.transportRequestUnit?.mailaddress ?? ''
    );

    useEffect(() => {
        if (!customerURLInformation?.urlId
            || customerURLInformation?.orderInfoType !== OrderInfoType.MaritimeOrderInfo) {
            navigate('/forbidden403Page');
        }
    }, [customerURLInformation, navigate]);

    const handleNavigateTopPage = async () => {
        const urlId = customerURLInformation?.urlId;
        if (!urlId) {
            navigate('/forbidden403Page');
            return;
        }
        await CustomerURLInformationUtil.instance.updateCustomerURLInformationByUrlId(urlId);
        navigate(`/${urlId}`);
    };

    const isUnitInvalid = (unitName: string, lastname: string, firstname: string, mail: string): boolean => {
        const unitMissing = unitName.trim() === '';
        const lastNameMissing = lastname.trim() === '';
        const firstNameMissing = firstname.trim() === '';
        const mailMissing = mail.trim() === '';
        return unitMissing || lastNameMissing || firstNameMissing || mailMissing;
    };

    const buildUpdatedOrder = (): MaritimeOrderInfo => {
        return {
            ...resolvedOrder!,
            requester: {
                ...resolvedOrder!.requester,
                supplyHeadquartersInfo: {
                    ...resolvedOrder!.requester.supplyHeadquartersInfo,
                    unitName: supplyHeadquartersUnitName,
                    lastname: supplyHeadquartersLastName,
                    firstname: supplyHeadquartersFirstName,
                    tel: supplyHeadquartersTel,
                    mailaddress: supplyHeadquartersMail,
                },
                supplySourceUnitInfo: {
                    ...resolvedOrder!.requester.supplySourceUnitInfo,
                    unitName: supplySourceUnitName,
                    lastname: supplySourceLastName,
                    firstname: supplySourceFirstName,
                    tel: supplySourceTel,
                    mailaddress: supplySourceMail,
                },
            },
            destination: {
                ...resolvedOrder!.destination,
                transportRequestUnit: {
                    ...resolvedOrder!.destination.transportRequestUnit,
                    unitName: transportRequestUnitName,
                    lastname: transportRequestLastName,
                    firstname: transportRequestFirstName,
                    tel: transportRequestTel,
                    mailaddress: transportRequestMail,
                },
            },
        };
    };

    const isNextDisabled = () => {
        return (
            isUnitInvalid(
                supplyHeadquartersUnitName,
                supplyHeadquartersLastName,
                supplyHeadquartersFirstName,
                supplyHeadquartersMail
            )
            || isUnitInvalid(supplySourceUnitName, supplySourceLastName, supplySourceFirstName, supplySourceMail)
            || isUnitInvalid(
                transportRequestUnitName,
                transportRequestLastName,
                transportRequestFirstName,
                transportRequestMail
            )
        );
    };

    const handleToBackPage = () => {
        if (!resolvedOrder) return;
        if (!customerURLInformation?.urlId) {
            navigate('/forbidden403Page');
            return;
        }
        const updated = buildUpdatedOrder();
        navigate('/ordergoodsanddestinationinput/' + customerURLInformation?.urlId, {
            state: {
                orderForEdit: updated,
            },
        });
    };

    const handleToNextPage = () => {
        if (!resolvedOrder) return;

        if (!customerURLInformation?.urlId) {
            navigate('/forbidden403Page');
            return;
        }

        markAllTouched();
        if (isNextDisabled()) return;

        const updated = buildUpdatedOrder();
        navigate('/orderconfirm/' + customerURLInformation?.urlId, {
            state: {
                order: updated
            },
        });
    };

    if (!resolvedOrder) {
        return (
            <OrderInfoNotFound
                appBarLabel="配送依頼"
                process={Process.ContactInput}
                message={`注文情報が取得できませんでした。\nブラウザ更新等により画面遷移時の情報が失われた可能性があります。`}
                onNavigateHome={handleNavigateTopPage}
            />
        );
    }

    return (
        <React.Fragment>
            <Grid container justifyContent="center" xs={12} spacing={1}>
                <Grid item xs={12}>
                    <ApplicationTooBar label="配送依頼" />
                </Grid>
                <Grid item xs={12}>
                    <OrderStepBar process={Process.ContactInput} />
                </Grid>

                <SectionGrid title="荷送人情報" icon={<LocalShippingIcon />}>
                    <Grid container spacing={3}>
                        {/* 補給本部 */}
                        <Grid item xs={12}>
                            <Typography color="primary" fontWeight="bold">補給本部</Typography>
                        </Grid>
                        <UnitContactFields
                            unitKey="hq"
                            unitName={supplyHeadquartersUnitName}
                            setUnitName={setSupplyHeadquartersUnitName}
                            lastName={supplyHeadquartersLastName}
                            setLastName={setSupplyHeadquartersLastName}
                            firstName={supplyHeadquartersFirstName}
                            setFirstName={setSupplyHeadquartersFirstName}
                            tel={supplyHeadquartersTel}
                            setTel={setSupplyHeadquartersTel}
                            mail={supplyHeadquartersMail}
                            setMail={setSupplyHeadquartersMail}
                            touched={touched}
                            markTouched={markTouched}
                            requiredMessage={REQUIRED_MESSAGE}
                        />

                        {/* 補給元部隊 */}
                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography color="primary" fontWeight="bold">補給元部隊</Typography>
                        </Grid>
                        <UnitContactFields
                            unitKey="src"
                            unitName={supplySourceUnitName}
                            setUnitName={setSupplySourceUnitName}
                            lastName={supplySourceLastName}
                            setLastName={setSupplySourceLastName}
                            firstName={supplySourceFirstName}
                            setFirstName={setSupplySourceFirstName}
                            tel={supplySourceTel}
                            setTel={setSupplySourceTel}
                            mail={supplySourceMail}
                            setMail={setSupplySourceMail}
                            touched={touched}
                            markTouched={markTouched}
                            requiredMessage={REQUIRED_MESSAGE}
                        />
                    </Grid>
                </SectionGrid>

                <SectionGrid title="荷受人情報" icon={<DirectionsBoatIcon />}>
                    <Grid container spacing={3}>
                        {/* 輸送要求部隊 */}
                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography color="primary" fontWeight="bold">輸送要求部隊</Typography>
                        </Grid>
                        <UnitContactFields
                            unitKey="dst"
                            unitName={transportRequestUnitName}
                            setUnitName={setTransportRequestUnitName}
                            lastName={transportRequestLastName}
                            setLastName={setTransportRequestLastName}
                            firstName={transportRequestFirstName}
                            setFirstName={setTransportRequestFirstName}
                            tel={transportRequestTel}
                            setTel={setTransportRequestTel}
                            mail={transportRequestMail}
                            setMail={setTransportRequestMail}
                            touched={touched}
                            markTouched={markTouched}
                            requiredMessage={REQUIRED_MESSAGE}
                        />
                    </Grid>
                </SectionGrid>
                <OrderBackNextButtons
                    onBack={() => handleToBackPage()}
                    onNext={() => handleToNextPage()}
                    nextDisabled={isNextDisabled()}
                />
            </Grid>
        </React.Fragment>
    );
}